import type { Box, BoxSize } from '#lib/server/dhl/dhl.entity'

export const MAIN_PAGE = '/main/orders'

export const BOX_SIZE: Record<BoxSize, Box> = {
  XL: {
    identifier: 'XL',
    dimensions: { length: 59, width: 39, height: 47 },
    maxQuantity: 23,
    weight: 23,
    name: 'Neo26'
  }, // Box3
  LG: {
    identifier: 'LG',
    dimensions: { length: 50, width: 40, height: 30 },
    maxQuantity: 12,
    weight: 12,
    name: 'Neo21'
  }, // Box2
  MD: { identifier: 'MD', dimensions: { length: 40, width: 30, height: 26 }, maxQuantity: 6, weight: 6, name: 'Neo15' }, // Box1
  SM: {
    identifier: 'SM',
    dimensions: { length: 40, width: 30, height: 17 },
    maxQuantity: 4,
    weight: 4,
    name: 'Neo15-X'
  }, // Box4
  XS: { identifier: 'XS', dimensions: { length: 25, width: 17, height: 10 }, maxQuantity: 1, weight: 1, name: 'Fiksu' } // "Fiksu"
}

export const WM_BOX: Box = {
  identifier: 'WM',
  dimensions: { length: 31, width: 22, height: 17 },
  maxQuantity: 1,
  weight: 2,
  name: 'WMBox'
}

export const DEVICE = {
  'S-as-OD-02-NN': 'Main meter optical reader',
  'S-as-OD-02-NN-E': 'Main meter optical reader, Elisa sim',
  'S-as-OD-02-NN-UK': 'Main meter optical reader UK',
  'S-as-OD-02-NN-US': 'Main meter optical reader US/CA',
  'S-as-OD-02-WN': 'Main meter weak network',
  'S-as-OD-02-WN-ETH': 'Main meter weak network, Ethernet',
  'S-as-OD-02-WN-SP2': 'Main meter weak network 2 meter package',
  'S-as-OD-02-WN-SP3': 'Main meter weak network 3 meter package',
  'S-as-OD-02-WN-SP4': 'Main meter weak network 4 meter package',
  'S-as-Wi-02-MN': 'MBus Device',
  'S-as-Wi-03-MN': 'Mbus Collection Device',
  'S-as-Wi-Ef-PN': 'Battery powered Pulse logger',
  'S-as-WL-02-MN': 'WMBus Device',
  'S-3DLA02A': 'Lora-adpapter for Zenner V2',
  'S-3DUA04A': 'Universal adapter ver.4',
  'S-CBEX01A': 'Extension Power Cable, 12VDC, 5m',
  'S-CBEX02A': 'Extension Power Cable, 12VDC, 10m',
  'S-CBEX03A': 'Extension Antenna Cable (SMA, White), 2m',
  'S-CBEX04A': 'Extension Antenna Cable (SMA, White), 5m',
  'S-ELEA01A': 'External 4G Antenna FS1000 + Fasteners (for the old router)',
  'S-ELEA03A': 'External 5G/4G/3G/GSM MIMO Antenna FS1500',
  'S-ELEF02A': 'Battery package for NB-IoT sensors, Efento 1S3P ER14505M',
  'S-ELMR04A': 'Mobile Router ARCHER-MR200, TP-Link',
  'S-ELPS01A': '12V1A6DC Power Supply, 5.5mm Plug',
  'S-IMRG02A': 'Rubber Grommet orig',
  'S-IMRG04A': 'Rubber Grommet high',
  'S-OMVT01A': 'Velcro Tape',
  'LL-OD-HW': 'LeakLook Optical Reader device',
  'FB-1': 'Water Flow Rate Bag',
  Other: 'Other'
} as const

export const CURRENCY = {
  EUR: 'EUR',
  SEK: 'SEK',
  NOK: 'NOK',
  GBP: 'GBP'
} as const
export type Currency = (typeof CURRENCY)[keyof typeof CURRENCY]

export const CURRENCY_SYMBOL = {
  EUR: '€',
  SEK: 'kr',
  NOK: 'kr',
  GBP: '£'
} as const

export const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE'
]

export const ORDER_TYPE_MAP = {
  'device-orders': ['Mail Order'],
  'storage-orders': ['Non-Billable Delivery Order'],
  'spare-parts': ['Other Billable Delivery Order']
}

export const formatter = {
  date: (input: string | Date) => {
    const d = new Date(input.toString())
    const day = `0${d.getDate()}`.slice(-2)
    const month = `0${d.getMonth() + 1}`.slice(-2)
    const year = d.getFullYear()

    return `${day}-${month}-${year}`
  },
  datepicker: (input: string | Date) => {
    const d = new Date(input.toString())
    const day = `0${d.getDate()}`.slice(-2)
    const month = `0${d.getMonth() + 1}`.slice(-2)
    const year = d.getFullYear()

    return `${year}-${month}-${day}`
  },
  datetime: (input?: string | Date) => {
    if (!input) return ' - '

    const d = new Date(input.toString())
    const day = `0${d.getDate()}`.slice(-2)
    const month = `0${d.getMonth() + 1}`.slice(-2)
    const year = d.getFullYear()
    const hour = `0${d.getHours()}`.slice(-2)
    const minute = `0${d.getMinutes()}`.slice(-2)

    return `${day}-${month}-${year} ${hour}:${minute}`
  },
  currency: (input: number) => {
    if (!input) return 0

    return Math.ceil(input)
  }
}

export function debounce<T>(callback: (value: T) => void, delay: number) {
  let timeout: NodeJS.Timeout

  return (value: T) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => callback(value), delay)
  }
}

export const scrollTo = (element: Element | null) => {
  if (!element) return

  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

export const isValidObjectId = (id: string) => /^[a-f\d]{24}$/i.test(id)

export const parseFetchResponse = (entry: string): Record<string, any> | null => {
  try {
    const parsed = JSON.parse(entry)
    if (!Array.isArray(parsed) || parsed.length <= 1 || typeof parsed[0] !== 'object') throw new Error('Invalid object')

    const result = Object.entries(parsed[0]).reduce(
      (acc, [key, value]) => {
        acc[key] = parsed[value as number]
        return acc
      },
      {} as Record<string, any>
    )

    return result
  } catch (error) {
    console.error('Error parsing entry:', error)
    return null
  }
}
